(ns ui.view.transaction-process.modals.common
  "Shared building blocks for the transaction-process form modals
  (decision/attestation/declaration). Each modal keeps its working draft in
  app-db under the TP state-path at `[:ui :modal-form]`, so it is scoped per
  process and disposed by the TP `::cleanup`. Only one modal is open per TP at
  a time, so the single sub-tree is enough."
  (:require
   [common.localization :refer [tr]]
   [frontend.logging :as logging]
   [re-frame.core :as rf]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.view.transaction-process.event :as tp-event]
   [ui.view.transaction-process.util :as tp-util]))

(def form-path [:ui :modal-form])

(defn form-full-path
  [state-path]
  (into state-path form-path))

(defn form-key-full-path
  [state-path k]
  (conj (form-full-path state-path) k))

(defn get-entity
  "Pure entity lookup over an injected entity map (the `:entities` value from
  the `::session-cofx/session-context` coeffect). Mirrors
  `frontend.state.current-session/get-entity`: returns the entity for an id, or
  the value itself if it's already an entity map."
  [entities eid]
  (or (get entities eid)
      (when (map? eid) eid)))

(def ^:private system-keys
  "System/derived `:common/*` (and internal) attributes that must never be echoed
  back to the backend on save. The decision modal loads whole existing entities
  into the edit draft, so without this they'd ride along on update — the backend
  rejects them anyway (decision/field-write-access?), but we keep payloads clean.

  This deliberately duplicates the app-wide notion (frontend.view.field-templates
  /system-fields, ui.view.card.sub) rather than deriving from schema authorization:
  the app-wide list mixes schema-system-write fields, user/guest-writable-but-internal
  fields, and visibility-hidden enrichment, so it isn't cleanly schema-derivable.
  A single shared source is worth doing, but as a separate (non-TP) cleanup.

  `:common/ord` is intentionally NOT included: reservations carry an explicit ord
  (prefill assigns by importance; manual adds get max+1) that must reach the backend
  on create so the list keeps a stable order across reloads. The backend's own
  `not-allowed` set blocks ord *updates* on existing entities, so we never
  accidentally reorder already-saved reservations."
  #{:common/disabled?
    :common/archived?
    :common/updated-on
    :common/updated-by
    :common/created-on
    :common/created-by
    :common/tenant
    :common/owner-role
    :common/migration-note
    :common/change-meta
    :common/zap-sample?
    :common/gift-sample?
    :common/search
    :common/entity-search
    :common/search-keywords
    :common/system-kws
    :common/related-entities
    :common/containing-entity
    :common/lower-case-str
    :lower-case-entity})

(def ^:private multi-value-fields
  #{:decision-participant/role})

(defn serialize-entity-for-backend
  "Strip system attributes and coerce single values on cardinality-many fields
  into vectors before sending to the backend."
  [entity]
  (let [cleaned (apply dissoc entity system-keys)]
    (reduce (fn [e field]
              (let [v (get e field)]
                (cond
                  (nil? v)        e
                  (sequential? v) e
                  (set? v)        e
                  :else           (assoc e field [v]))))
            cleaned
            multi-value-fields)))

(defn close-fx
  "Effect vector that closes the open modal (and its draft). Shared by every
  form modal's cancel and save-success paths. The detail panel (side peek) the
  modal was launched from is intentionally left open, so after editing a
  sub-entity the user returns to its peek showing the just-saved values (the
  panel re-resolves the entity live — see detail-panel/live-entity)."
  [ctx]
  [[:dispatch [::tp-event/close-modal ctx]]])

(defn save-success-effects
  [ctx]
  {:fx (close-fx ctx)})

(defn save-error-db
  [db {:keys [state-path]} err {:keys [log-message fallback-tr-key]}]
  (logging/error log-message err)
  (update-in db (form-full-path state-path)
             assoc
             :submitting? false
             :error (or (:message err) (tr fallback-tr-key))))

(rf/reg-event-fx
 ::close
 [rf/trim-v]
 (fn [_ [ctx]]
   {:fx (close-fx ctx)}))

(rf/reg-fx
 ::run-action
 ;; `action` is a thunk of shape (fn [success! error!]) that closes over its
 ;; payload and calls the relevant `frontend.actions.*` function. success/error
 ;; are re-frame dispatch vectors; the action's response/error is conj'd on.
 (fn [{:keys [action on-success on-error]}]
   (action (fn [resp] (when on-success (rf/dispatch (conj on-success resp))))
           (fn [err]  (when on-error (rf/dispatch (conj on-error err)))))))

(defn save-footer
  "Cancel / Save draft / Save ready footer shared by the decision, attestation
  and declaration modals. `ready-errors` (when non-empty) disables Save ready
  and drives its tooltip.

  `save-draft-tooltip` / `save-ready-tooltip` explain a disabled button that
  `ready-errors` does not account for — a permission gate, say. A disabled
  control always has to say why it is disabled, so pass the tooltip alongside
  whatever sets the matching `-disabled?` flag. An explicit ready tooltip wins
  over the `ready-errors` one."
  [{:keys [submitting? save-draft-disabled? save-ready-disabled? ready-errors
           save-draft-tooltip save-ready-tooltip
           on-cancel on-save-draft on-save-ready]}]
  [modal/footer-children-left-and-right
   [[button/secondary-hollow
     {:on-click on-cancel}
     (tr :cancel)]]
   [[button/secondary-hollow
     {:on-click  on-save-draft
      :disabled? (boolean (or submitting? save-draft-disabled?))
      :tooltip   save-draft-tooltip}
     (tr :transaction-process/save-draft)]
    [button/primary-solid
     {:on-click  on-save-ready
      :disabled? (boolean (or submitting? save-ready-disabled? (seq ready-errors)))
      :tooltip   (or save-ready-tooltip
                     (when (seq ready-errors)
                       (tp-util/ready-errors-tooltip ready-errors)))}
     (tr :transaction-process/save-ready)]]])

(defn error-banner
  [error]
  (when error
    [:div {:class "rounded border border-red-200 bg-red-50 px-3 py-2 text-sm m-0 text-red-700"}
     error]))

(defn type-label-header
  [label]
  (when label
    [:h4 {:class "text-base m-0 font-semibold text-gray-900"}
     label]))
